import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { AlertTriangle, Palette, Send, Briefcase, RefreshCw, Maximize } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";

const OWNER_ACCENT = ["bg-sand-deep", "bg-secondary", "bg-sand-deep", "bg-secondary"];

function OwnerColumn({ owner, top_tasks, other_tasks_count, overdue_jobs, missing_artwork, ready_to_send, open_jobs, index }) {
  const priorityColor = {
    High: "text-terracotta border-terracotta/40 bg-terracotta/10",
    Normal: "text-espresso border-espresso/40 bg-espresso/5",
    Low: "text-muted-foreground border-border bg-muted",
  };

  const hasNothing =
    top_tasks.length === 0 &&
    overdue_jobs.length === 0 &&
    other_tasks_count === 0 &&
    missing_artwork === 0 &&
    ready_to_send === 0;

  return (
    <Card
      className="card-elev overflow-hidden animate-fade-up"
      style={{ animationDelay: `${index * 80}ms` }}
      data-testid={`standup-owner-${owner}`}
    >
      <div className={`h-2 ${OWNER_ACCENT[index % OWNER_ACCENT.length]}`} />
      <CardContent className="p-6 space-y-6">
        <div className="flex items-baseline justify-between">
          <h3 className="font-heading text-3xl tracking-tight">{owner}</h3>
          <span className="label-caps">{open_jobs} open</span>
        </div>

        {/* Top 3 */}
        <div>
          <div className="label-caps mb-3">Top Priorities</div>
          {top_tasks.length === 0 ? (
            <div className="text-sm text-muted-foreground italic">No top-3 set</div>
          ) : (
            <ol className="space-y-2.5">
              {top_tasks.map((t, i) => (
                <li key={t.id} className="flex items-start gap-3" data-testid={`standup-${owner}-task-${i + 1}`}>
                  <span className="mt-0.5 font-heading text-lg text-espresso/70 w-5">
                    {t.top_priority_rank || i + 1}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium leading-snug">{t.title}</div>
                    <div className="mt-1 flex items-center gap-2">
                      {t.scheduled_time && (
                        <span className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                          {t.scheduled_time}
                        </span>
                      )}
                      {t.priority && (
                        <span className={`text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-full border ${priorityColor[t.priority] || priorityColor.Normal}`}>
                          {t.priority}
                        </span>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ol>
          )}
          {other_tasks_count > 0 && (
            <div className="mt-3 text-xs text-muted-foreground">+ {other_tasks_count} other task{other_tasks_count === 1 ? "" : "s"}</div>
          )}
        </div>

        {/* Overdue */}
        <div>
          <div className="label-caps mb-3 flex items-center gap-1.5 text-terracotta">
            <AlertTriangle className="h-3 w-3" /> Overdue
          </div>
          {overdue_jobs.length === 0 ? (
            <div className="text-sm text-muted-foreground italic">Nothing overdue</div>
          ) : (
            <ul className="space-y-2">
              {overdue_jobs.slice(0, 5).map((j) => (
                <li key={j.id} className="flex items-center justify-between text-sm" data-testid={`standup-${owner}-overdue-${j.id}`}>
                  <span className="truncate mr-2">{j.job_name}</span>
                  <span className="text-[10px] uppercase tracking-[0.18em] text-terracotta shrink-0">
                    {j.due_date}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Small stats row */}
        <div className="grid grid-cols-3 gap-2 pt-2 border-t border-border/60">
          <StatMini icon={Palette} label="Art" value={missing_artwork} />
          <StatMini icon={Send} label="Ready" value={ready_to_send} />
          <StatMini icon={Briefcase} label="Jobs" value={open_jobs} />
        </div>

        {hasNothing && <div className="text-xs text-center text-muted-foreground italic pt-2">All clear ☕</div>}
      </CardContent>
    </Card>
  );
}

function StatMini({ icon: Icon, label, value }) {
  return (
    <div className="text-center">
      <Icon className="h-3.5 w-3.5 mx-auto text-muted-foreground" />
      <div className="font-heading text-2xl mt-1 leading-none">{value}</div>
      <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mt-1">{label}</div>
    </div>
  );
}

export default function Standup() {
  const [data, setData] = useState({ date: "", owners: [] });
  const [loading, setLoading] = useState(true);
  const [fullscreen, setFullscreen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/standup");
      setData(data);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 60000); // auto-refresh every minute
    return () => clearInterval(t);
  }, [load]);

  const today = data.date
    ? new Date(data.date + "T00:00:00").toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })
    : "";

  return (
    <div className={fullscreen ? "fixed inset-0 z-50 bg-sand overflow-auto" : "max-w-[1600px] mx-auto px-8 py-10"}>
      <div className={fullscreen ? "px-10 py-10" : ""}>
        <div className="flex items-end justify-between gap-6 mb-8 animate-fade-up">
          <div>
            <div className="label-caps mb-2">Studio Standup</div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
              {today || "Today"}
            </h1>
            <p className="mt-3 text-muted-foreground max-w-2xl">
              A shared morning read of the shop — each owner's top priorities and overdue work at a glance.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={load}
              className="inline-flex items-center gap-1.5 border border-border px-4 py-2 text-[11px] uppercase tracking-[0.22em] text-muted-foreground hover:bg-muted"
              data-testid="standup-refresh"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
            </button>
            <button
              onClick={() => setFullscreen((v) => !v)}
              className="inline-flex items-center gap-1.5 border border-espresso/70 px-4 py-2 text-[11px] uppercase tracking-[0.22em] text-espresso hover:bg-espresso hover:text-sand"
              data-testid="standup-fullscreen"
            >
              <Maximize className="h-3.5 w-3.5" /> {fullscreen ? "Exit" : "Wall Mode"}
            </button>
          </div>
        </div>

        {!loading && data.owners.length === 0 && (
          <Card className="card-elev">
            <CardContent className="p-10 text-center text-muted-foreground">
              No owners configured. Add team members in{" "}
              <Link to="/setup" className="underline underline-offset-4 text-espresso">Setup Lists</Link>.
            </CardContent>
          </Card>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5">
          {data.owners.map((o, i) => (
            <OwnerColumn key={o.owner} index={i} {...o} />
          ))}
        </div>

        {fullscreen && (
          <div className="mt-8 flex justify-end">
            <button
              onClick={() => setFullscreen(false)}
              className="text-xs text-muted-foreground underline underline-offset-4"
            >
              Exit wall mode
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
